// Cronómetro de llamada (PRD Bloque L). Vive en localStorage, no en React state: la duración
// siempre se calcula como "ahora - inicio" (menos lo pausado), nunca con un contador, para que
// siga siendo correcto si el celular sale del navegador para marcar o si se recarga la página.
import { useEffect, useState } from "react";

const KEY = "raftel.call_timer";

type TimerState = {
  startedAt: number; // epoch ms
  pausedAt: number | null; // epoch ms si está pausado ahora mismo
  pausedMs: number; // total ya acumulado de pausas anteriores
};

const read = (): TimerState | null => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "null");
  } catch {
    return null;
  }
};
const write = (s: TimerState | null) => {
  if (s) localStorage.setItem(KEY, JSON.stringify(s));
  else localStorage.removeItem(KEY);
  window.dispatchEvent(new Event("raftel-call-timer"));
};

/** Segundos transcurridos, sin contar el tiempo en pausa. */
export const elapsedSeconds = (s: TimerState, now = Date.now()): number => {
  const pausedNow = s.pausedAt ? now - s.pausedAt : 0;
  return Math.max(
    0,
    Math.round((now - s.startedAt - s.pausedMs - pausedNow) / 1000),
  );
};

export const startCallTimer = () =>
  write({ startedAt: Date.now(), pausedAt: null, pausedMs: 0 });

export const pauseCallTimer = () => {
  const s = read();
  if (s && !s.pausedAt) write({ ...s, pausedAt: Date.now() });
};

export const resumeCallTimer = () => {
  const s = read();
  if (s?.pausedAt)
    write({
      ...s,
      pausedAt: null,
      pausedMs: s.pausedMs + (Date.now() - s.pausedAt),
    });
};

/** Detiene y borra el cronómetro; devuelve los segundos finales (o null si no había uno activo). */
export const stopCallTimer = (): number | null => {
  const s = read();
  if (!s) return null;
  const seconds = elapsedSeconds(s);
  write(null);
  return seconds;
};

export const clearCallTimer = () => write(null);

/** Estado en vivo del cronómetro, para mostrarlo en cualquier pantalla. */
export const useCallTimer = () => {
  const [state, setState] = useState<TimerState | null>(() => read());
  const [, tick] = useState(0);

  useEffect(() => {
    const onChange = () => setState(read());
    window.addEventListener("raftel-call-timer", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("raftel-call-timer", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  useEffect(() => {
    if (!state || state.pausedAt) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [state]);

  return {
    active: !!state,
    paused: !!state?.pausedAt,
    seconds: state ? elapsedSeconds(state) : 0,
    pause: pauseCallTimer,
    resume: resumeCallTimer,
  };
};

export const fmtDuration = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};
